import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import CodeInput from './CodeInput';
import PlatformMessagesBanner from './PlatformMessagesBanner';
import { api } from '../services/api';
import '../styles/Signup.css';
import {
  getHostKind,
  getPortalLabelFromReturnTo,
  getReturnToParam,
} from '../utils/host';
import { isMicrosoftOAuthEnabled } from '../utils/env';
import { getStoredItem, removeStoredItem } from '../utils/storage';
import finbaseLogo from '../assets/logos/finbase-logo.svg';
import QRCode from 'qrcode';

const ENABLE_MICROSOFT_OAUTH = isMicrosoftOAuthEnabled();

const getApiErrorMessage = (err, fallback) => (
  err.response?.data?.message || err.response?.data?.error || fallback
);

const getPortalAccessErrorMessage = (err) => {
  const apiMessage = getApiErrorMessage(err, '');
  if (apiMessage && !/^access denied\.?$/i.test(apiMessage.trim())) {
    return apiMessage;
  }
  return 'You do not have access to the requested portal.';
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [mfaChannel, setMfaChannel] = useState('email');
  const [mfaPhone, setMfaPhone] = useState('');
  const [mfaPhoneMasked, setMfaPhoneMasked] = useState('');
  const [mfaLoginMethod, setMfaLoginMethod] = useState(null);
  const [step, setStep] = useState('email');
  const [mfaResendCooldown, setMfaResendCooldown] = useState(0);
  const [totpSecret, setTotpSecret] = useState('');
  const [totpUri, setTotpUri] = useState('');
  const [totpQrData, setTotpQrData] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [totpBackupCodes, setTotpBackupCodes] = useState([]);
  const [totpSetupLoading, setTotpSetupLoading] = useState(false);
  const [totpVerifyLoading, setTotpVerifyLoading] = useState(false);
  const [totpLoginTokens, setTotpLoginTokens] = useState(null);
  const [totpSetupComplete, setTotpSetupComplete] = useState(false);
  const [pendingForcePassword, setPendingForcePassword] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpResendCooldown, setOtpResendCooldown] = useState(0);
  const [otpShake, setOtpShake] = useState(false);
  const [mfaShake, setMfaShake] = useState(false);
  const [forcePassword, setForcePassword] = useState('');
  const [forcePasswordConfirm, setForcePasswordConfirm] = useState('');
  const [forcePasswordToken, setForcePasswordToken] = useState('');
  const [forcePasswordReason, setForcePasswordReason] = useState('reset');
  const [forcePasswordLoading, setForcePasswordLoading] = useState(false);
  const passwordInputRef = useRef(null);
  const [error, setError] = useState('');
  const [authOptions, setAuthOptions] = useState([]);
  const [authLoading, setAuthLoading] = useState(false);
  const [supportHandoffLoading, setSupportHandoffLoading] = useState(false);
  const [hasPendingInvite, setHasPendingInvite] = useState(false);
  const { login, signupComplete } = React.useContext(AuthContext);
  const history = useHistory();
  const hostKind = getHostKind();
  const returnTo = getReturnToParam();
  const portalLabel = hostKind === 'id' ? getPortalLabelFromReturnTo(returnTo) : null;
  const isSupportReturn = hostKind === 'id' && returnTo === 'support';
  const isDemoReturn = hostKind === 'id' && returnTo === 'demo';

  const beginSupportHandoff = () => {
    if (isSupportReturn) setSupportHandoffLoading(true);
  };

  const endSupportHandoff = () => {
    if (isSupportReturn) setSupportHandoffLoading(false);
  };

  useEffect(() => {
    if (getStoredItem('finbase.auth_expired') === '1') {
      setError('Your session has expired. Please sign in again.');
      removeStoredItem('finbase.auth_expired');
    }
  }, []);

  useEffect(() => {
    if (hostKind !== 'id') return;
    const params = new URLSearchParams(window.location.search);
    const mfaFlag = params.get('mfa');
    const mfaTokenParam = params.get('mfa_token');
    const mfaChannelParam = params.get('mfa_channel');
    const forceFlag = params.get('force_password') === '1';
    const resetTokenParam = params.get('reset_token');
    const resetReasonParam = params.get('reset_reason');
    const emailParam = params.get('email');
    const errorParam = params.get('error');
    if (emailParam) setEmail(emailParam);
    if (errorParam === 'support_sso_disabled') {
      setError('Support portal sign-in is not enabled for this environment.');
    }
    if (forceFlag && resetTokenParam) {
      setForcePasswordToken(resetTokenParam);
      setForcePasswordReason(resetReasonParam || 'reset');
      setStep('force-password');
      return;
    }
    if (mfaFlag !== '1' || !mfaTokenParam || !mfaChannelParam) return;
    setMfaToken(mfaTokenParam);
    setMfaChannel(mfaChannelParam);
    if (mfaChannelParam === 'totp') {
      const setupRequired = params.get('totp_setup_required') === '1';
      setTotpSecret('');
      setTotpUri('');
      setTotpQrData('');
      setTotpCode('');
      setTotpBackupCodes([]);
      setTotpLoginTokens(null);
      setTotpSetupComplete(false);
      setStep(setupRequired ? 'totp-setup' : 'totp-verify');
      return;
    }
    if (mfaChannelParam === 'sms' && params.get('phone_required') === '1') {
      setStep('mfa-phone');
      return;
    }
    if (mfaChannelParam === 'sms') {
      setMfaPhoneMasked(params.get('phone_masked') || '');
      setMfaResendCooldown(30);
      setStep('mfa');
      return;
    }
    setMfaResendCooldown(30);
    setStep('mfa');
  }, [hostKind]);

  const redirectAfterAuth = (accessToken, refreshToken, sessionMetadata = {}) => {
    if (sessionMetadata?.redirect_url) {
      beginSupportHandoff();
      window.location.replace(sessionMetadata.redirect_url);
      return;
    }
    if (!accessToken || !refreshToken) return;
    if (hostKind === 'id') return;
    signupComplete(accessToken, refreshToken, sessionMetadata);
    history.push('/account');
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setAuthLoading(true);
    try {
      const response = await api.post('/acct-mgmt/v1/auth/options', { email, flow: 'login', return_to: returnTo });
      if (!response.data?.has_account && !response.data?.has_invite) {
        if (isDemoReturn) {
          setError('Demo Portal team member access requires an active Finbase internal account.');
          return;
        }
        history.push(`/signup?email=${encodeURIComponent(email)}`);
        return;
      }
      setHasPendingInvite(Boolean(response.data?.has_invite));
      const options = (response.data?.options || []).filter((option) => ENABLE_MICROSOFT_OAUTH || option.code !== 'microsoft');
      if (options.length === 0 && response.data?.has_account && response.data?.password_unset) {
        setError('No sign-in methods are available for this account. Please contact your account administrator.');
        return;
      }
      if (options.length === 0) {
        setError('No sign-in methods are available for this account.');
        return;
      }
      setAuthOptions(options);
      const passwordOption = options.find((option) => option.type === 'password');
      if (options.length === 1 && passwordOption) {
        setStep('password');
        return;
      }
      setStep('choose');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load sign-in options'));
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMfaLoginMethod('password');
    beginSupportHandoff();
    try {
      const data = await login(email, password, returnTo);
      if (data?.force_password_reset) {
        endSupportHandoff();
        setForcePasswordToken(data.reset_token || '');
        setForcePasswordReason(data.reset_reason || 'reset');
        setStep('force-password');
        return;
      }
      if (data?.mfa_required) {
        endSupportHandoff();
        setMfaChannel(data.mfa_channel || 'email');
        if (data.mfa_channel === 'totp') {
          setMfaToken(data.mfa_token);
          setTotpSecret('');
          setTotpUri('');
          setTotpQrData('');
          setTotpCode('');
          setTotpBackupCodes([]);
          setTotpLoginTokens(null);
          setTotpSetupComplete(false);
          setStep(data.totp_setup_required ? 'totp-setup' : 'totp-verify');
          return;
        }
        if (data.mfa_channel === 'sms' && data.phone_required) {
          setStep('mfa-phone');
        } else {
          setMfaToken(data.mfa_token);
          setMfaPhoneMasked(data.phone_masked || '');
          setMfaResendCooldown(30);
          setStep('mfa');
        }
        return;
      }
      if (data?.invite_code) {
        endSupportHandoff();
        history.push(`/signup?email=${encodeURIComponent(email)}&invite_code=${data.invite_code}&resume=1`);
        return;
      }
      if (data?.needs_org_setup) {
        endSupportHandoff();
        history.push(`/signup?email=${encodeURIComponent(email)}&resume=1`);
        return;
      }
      if (data?.redirect_url || (data?.access_token && data?.refresh_token)) {
        redirectAfterAuth(data.access_token, data.refresh_token, data);
        return;
      }
      endSupportHandoff();
      history.push('/account');
    } catch (err) {
      endSupportHandoff();
      if (err.response?.status === 403) {
        setError(getPortalAccessErrorMessage(err));
      } else {
        setError(getApiErrorMessage(err, 'Login failed'));
      }
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');
    beginSupportHandoff();
    try {
      const response = await api.post('/acct-mgmt/v1/login/mfa/verify', { email, code: mfaCode, mfa_token: mfaToken, return_to: returnTo });
      if (response.data?.force_password_reset) {
        endSupportHandoff();
        setForcePasswordToken(response.data.reset_token || '');
        setForcePasswordReason(response.data.reset_reason || 'reset');
        setStep('force-password');
        return;
      }
      if (response.data?.redirect_url || (response.data.access_token && response.data.refresh_token)) {
        redirectAfterAuth(response.data.access_token, response.data.refresh_token, response.data);
        return;
      }
      if (response.data.invite_code) {
        endSupportHandoff();
        history.push(`/signup?email=${encodeURIComponent(email)}&invite_code=${response.data.invite_code}&resume=1`);
        return;
      }
      if (response.data.needs_org_setup) {
        endSupportHandoff();
        history.push(`/signup?email=${encodeURIComponent(email)}&resume=1`);
        return;
      }
      endSupportHandoff();
      history.push('/account');
    } catch (err) {
      endSupportHandoff();
      if (err.response?.status === 403) {
        setError(getPortalAccessErrorMessage(err));
      } else {
        setError(getApiErrorMessage(err, 'MFA verification failed'));
      }
      setMfaCode('');
      setMfaShake(true);
      setTimeout(() => setMfaShake(false), 500);
    }
  };

  const handleMfaPhoneRequest = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const response = await api.post('/acct-mgmt/v1/login/mfa/phone/request', {
        email,
        phone_number: mfaPhone,
        return_to: returnTo,
        login_method: mfaLoginMethod,
      });
      setMfaToken(response.data?.mfa_token || '');
      setMfaChannel('sms');
      setMfaPhoneMasked(response.data?.phone_masked || '');
      setMfaResendCooldown(30);
      setStep('mfa');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to send verification code.'));
    }
  };

  const loadTotpSetup = async () => {
    setTotpSetupLoading(true);
    setError('');
    try {
      const response = await api.post('/acct-mgmt/v1/login/mfa/totp/setup', { email, mfa_token: mfaToken, return_to: returnTo });
      setTotpSecret(response.data?.secret || '');
      setTotpUri(response.data?.otpauth_uri || '');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to start authenticator setup'));
    } finally {
      setTotpSetupLoading(false);
    }
  };

  const handleTotpVerify = async (e) => {
    e.preventDefault();
    setError('');
    setTotpVerifyLoading(true);
    beginSupportHandoff();
    try {
      const response = await api.post('/acct-mgmt/v1/login/mfa/totp/verify', {
        email,
        code: totpCode,
        mfa_token: mfaToken,
        return_to: returnTo,
      });
      if (response.data?.backup_codes?.length) {
        endSupportHandoff();
        setTotpBackupCodes(response.data.backup_codes);
        if (response.data.force_password_reset) {
          setPendingForcePassword({
            reset_token: response.data.reset_token,
            reset_reason: response.data.reset_reason || 'reset',
          });
        } else {
          setTotpLoginTokens(response.data);
        }
        setTotpSetupComplete(true);
        return;
      }
      if (response.data?.force_password_reset) {
        endSupportHandoff();
        setForcePasswordToken(response.data.reset_token || '');
        setForcePasswordReason(response.data.reset_reason || 'reset');
        setStep('force-password');
        return;
      }
      if (response.data?.redirect_url || (response.data?.access_token && response.data?.refresh_token)) {
        redirectAfterAuth(response.data.access_token, response.data.refresh_token, response.data);
        return;
      }
      endSupportHandoff();
    } catch (err) {
      endSupportHandoff();
      if (err.response?.status === 403) {
        setError(getPortalAccessErrorMessage(err));
      } else {
        setError(getApiErrorMessage(err, 'Authenticator verification failed'));
      }
      setTotpCode('');
      setMfaShake(true);
      setTimeout(() => setMfaShake(false), 500);
    } finally {
      setTotpVerifyLoading(false);
    }
  };

  const handleForcePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!forcePassword || !forcePasswordConfirm) {
      setError('Enter and confirm your new password.');
      return;
    }
    if (forcePassword !== forcePasswordConfirm) {
      setError('Passwords do not match.');
      return;
    }
    setForcePasswordLoading(true);
    beginSupportHandoff();
    try {
      const response = await api.post('/acct-mgmt/v1/login/password/force/complete', {
        email,
        reset_token: forcePasswordToken,
        password: forcePassword,
        confirm_password: forcePasswordConfirm,
        return_to: returnTo,
      });
      if (response.data?.redirect_url || (response.data?.access_token && response.data?.refresh_token)) {
        redirectAfterAuth(response.data.access_token, response.data.refresh_token, response.data);
        return;
      }
      if (response.data?.invite_code) {
        endSupportHandoff();
        history.push(`/signup?email=${encodeURIComponent(email)}&invite_code=${response.data.invite_code}&resume=1`);
        return;
      }
      if (response.data?.needs_org_setup) {
        endSupportHandoff();
        history.push(`/signup?email=${encodeURIComponent(email)}&resume=1`);
        return;
      }
      endSupportHandoff();
      history.push('/account');
    } catch (err) {
      endSupportHandoff();
      setError(getApiErrorMessage(err, 'Failed to update password.'));
    } finally {
      setForcePasswordLoading(false);
    }
  };

  useEffect(() => {
    if (mfaResendCooldown <= 0) return;
    const timer = setInterval(() => {
      setMfaResendCooldown((prev) => Math.max(prev - 1, 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [mfaResendCooldown]);

  useEffect(() => {
    if (otpResendCooldown <= 0) return;
    const timer = setInterval(() => {
      setOtpResendCooldown((prev) => Math.max(prev - 1, 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [otpResendCooldown]);

  useEffect(() => {
    if (step === 'password' && passwordInputRef.current) {
      passwordInputRef.current.focus();
    }
  }, [step]);

  useEffect(() => {
    if (step === 'totp-setup' && !totpUri && !totpSetupLoading) {
      loadTotpSetup();
    }
  }, [step, totpUri, totpSetupLoading]);

  useEffect(() => {
    if (!totpUri) {
      setTotpQrData('');
      return;
    }
    QRCode.toDataURL(totpUri, { width: 240, margin: 1 })
      .then((url) => setTotpQrData(url))
      .catch(() => setTotpQrData(''));
  }, [totpUri]);

  return (
    <div className="auth-page">
      <PlatformMessagesBanner surface="id_portal" />
      <div className="card auth-card shadow-sm">
        <div className="auth-logo">
          <img src={finbaseLogo} alt="Finbase" />
        </div>
        <h1 className="h3 fw-bold text-dark mb-2 text-center">Log In to Continue</h1>
        {portalLabel && (
          <div className="text-muted small mb-3 text-center">Signing in to: {portalLabel}</div>
        )}
        {error && <p className="text-danger text-center mb-3">{error}</p>}
        {supportHandoffLoading && (
          <div className="text-center py-3" role="status" aria-live="polite">
            <div className="spinner-border text-primary mb-3" aria-hidden="true"></div>
            <h2 className="h5 fw-bold text-dark mb-2">Signing You Into Support Portal</h2>
            <p className="text-muted mb-0">Preparing your support workspace...</p>
          </div>
        )}
        {!supportHandoffLoading && step === 'email' && (
          <form onSubmit={handleEmailSubmit}>
            <div className="mb-3">
              <label className="form-label">Email Address</label>
                <input
                  type="email"
                  className="form-control"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
            </div>
            <button type="submit" className="btn btn-primary w-100" disabled={authLoading}>
              {authLoading ? 'Checking...' : 'Continue'}
            </button>
          </form>
        )}
        {!supportHandoffLoading && step === 'choose' && (
          <div>
            <div className="mb-3 text-muted text-center">Choose a sign-in method for {email}.</div>
            <div className="d-flex flex-column gap-2">
              {authOptions.map((option) => {
                if (option.type === 'password') {
                  return (
                    <button
                      key={option.code}
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setStep('password')}
                    >
                      {option.label}
                    </button>
                  );
                }
                if (option.type === 'otp') {
                  return (
                    <button
                      key={option.code}
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setStep('otp')}
                    >
                      {option.label}
                    </button>
                  );
                }
                const iconClass = option.code === 'google'
                  ? 'fab fa-google'
                  : option.code === 'microsoft'
                    ? 'fab fa-microsoft'
                    : option.code === 'apple'
                      ? 'fab fa-apple'
                      : option.code === 'atlassian'
                        ? 'fab fa-atlassian'
                        : null;
                return (
                  <a key={option.code} href={option.url} className="btn btn-outline-secondary">
                    {iconClass && <i className={`${iconClass} me-2`} aria-hidden="true"></i>}
                    {option.label}
                  </a>
                );
              })}
            </div>
            <button
              type="button"
              className="btn btn-link mt-3 p-0"
              onClick={() => {
                setStep('email');
                setPassword('');
                setAuthOptions([]);
                setResolvedOrgName('');
                setHasPendingInvite(false);
              }}
            >
              Use a different email
            </button>
          </div>
        )}
        {!supportHandoffLoading && step === 'password' && (
          <form onSubmit={handleSubmit}>
            <div className="mb-2 text-muted">Signing in as {email}</div>
            <div className="mb-3">
              <label className="form-label">Password</label>
                <input
                  type="password"
                  className="form-control"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  ref={passwordInputRef}
                />
            </div>
            <button type="submit" className="btn btn-primary w-100">Log In</button>
            <div className="text-center mt-2">
              <a className="text-primary" href={`/password-reset?email=${encodeURIComponent(email)}`}>
                Forgot password?
              </a>
            </div>
            <button
              type="button"
              className="btn btn-link mt-2 w-100"
              onClick={() => {
                setStep('email');
                setPassword('');
                setAuthOptions([]);
                setResolvedOrgName('');
                setHasPendingInvite(false);
              }}
            >
              Use a different email
            </button>
          </form>
        )}
        {!supportHandoffLoading && step === 'mfa-phone' && (
          <form onSubmit={handleMfaPhoneRequest}>
            <div className="mb-2 text-muted text-center">
              Add your phone number to receive a verification code.
            </div>
            <div className="mb-3">
              <label className="form-label">Phone Number</label>
              <input
                className="form-control"
                value={mfaPhone}
                onChange={(e) => setMfaPhone(e.target.value)}
                required
              />
              <div className="form-text text-muted">US numbers only.</div>
              <div className="form-text text-muted">
                By enabling SMS verification, you consent to receive security codes by text message. Message and data rates may apply.
              </div>
            </div>
            <button type="submit" className="btn btn-primary w-100">
              Send Code
            </button>
          </form>
        )}
        {!supportHandoffLoading && step === 'mfa' && (
          <form onSubmit={handleMfaSubmit}>
            <div className="mb-2 text-muted text-center">
              {mfaChannel === 'sms'
                ? `Enter the verification code sent to ${mfaPhoneMasked || 'your phone'}.`
                : `Enter the verification code sent to ${email}.`}
            </div>
            <div className="mb-3 text-center">
              <label className="form-label text-center d-block">Verification Code</label>
              <CodeInput value={mfaCode} onChange={setMfaCode} shake={mfaShake} autoFocus />
            </div>
            <button type="submit" className="btn btn-primary w-100" disabled={mfaCode.length < 6}>Verify</button>
            <button
              type="button"
              className="btn btn-outline-secondary w-100 mt-2"
              disabled={mfaResendCooldown > 0}
              onClick={async () => {
                setError('');
                try {
                  await api.post('/acct-mgmt/v1/login/mfa/resend', { email, mfa_token: mfaToken });
                  setMfaResendCooldown(30);
                } catch (err) {
                  setError(getApiErrorMessage(err, 'Failed to resend code'));
                }
              }}
            >
              {mfaResendCooldown > 0 ? `Resend Code (${mfaResendCooldown}s)` : 'Resend Code'}
            </button>
          </form>
        )}
        {!supportHandoffLoading && step === 'totp-setup' && (
          <form onSubmit={handleTotpVerify}>
            <div className="mb-2 text-muted text-center">
              {totpSetupComplete ? 'Authenticator app set up successfully.' : 'Set up your authenticator app to continue.'}
            </div>
            {totpSetupLoading && (
              <div className="text-muted text-center mb-3">Loading setup details...</div>
            )}
            {!totpSetupLoading && (
              <>
                {!totpSetupComplete && (
                  <>
                    <div className="text-muted small mb-2 text-center">
                      Use any authenticator app (Apple Passwords, Google Authenticator, Microsoft Authenticator, Authy, 1Password, Bitwarden, Duo).
                    </div>
                    {totpQrData ? (
                      <div className="text-center mb-3">
                        <img src={totpQrData} alt="Authenticator QR code" />
                      </div>
                    ) : null}
                    {totpSecret && (
                      <div className="mb-3 text-center">
                        <div className="text-muted small mb-1">Manual setup key</div>
                        <div className="fw-semibold">{totpSecret}</div>
                      </div>
                    )}
                    <div className="mb-3 text-center">
                      <label className="form-label text-center d-block">Verification Code</label>
                      <input
                        className="form-control text-center"
                        value={totpCode}
                        onChange={(e) => setTotpCode(e.target.value)}
                        placeholder="6-digit code"
                        autoFocus
                      />
                    </div>
                  </>
                )}
                {totpBackupCodes.length > 0 && (
                  <div className="mb-3">
                    <div className="fw-semibold mb-2 text-center">Backup Codes</div>
                    <div className="d-flex flex-column gap-1 text-center">
                      {totpBackupCodes.map((code) => (
                        <div key={code} className="font-monospace">{code}</div>
                      ))}
                    </div>
                    <div className="text-muted small mt-2 text-center">Save these codes now. They will not be shown again.</div>
                  </div>
                )}
                {!totpSetupComplete && (
                  <button type="submit" className="btn btn-primary w-100" disabled={!totpCode || totpVerifyLoading}>
                    {totpVerifyLoading ? 'Verifying...' : 'Verify & Continue'}
                  </button>
                )}
                {(totpLoginTokens || pendingForcePassword) && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary w-100 mt-2"
                    onClick={() => {
                      if (pendingForcePassword) {
                        setForcePasswordToken(pendingForcePassword.reset_token || '');
                        setForcePasswordReason(pendingForcePassword.reset_reason || 'reset');
                        setPendingForcePassword(null);
                        setStep('force-password');
                        return;
                      }
                      if (totpLoginTokens) {
                        redirectAfterAuth(totpLoginTokens.access_token, totpLoginTokens.refresh_token, totpLoginTokens);
                      }
                    }}
                  >
                    Continue
                  </button>
                )}
              </>
            )}
          </form>
        )}
        {!supportHandoffLoading && step === 'totp-verify' && (
          <form onSubmit={handleTotpVerify}>
            <div className="mb-2 text-muted text-center">
              Enter the code from your authenticator app.
            </div>
            <div className="mb-3 text-center">
              <input
                className="form-control text-center"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                placeholder="6-digit code or backup code"
                autoFocus
              />
            </div>
            <button type="submit" className="btn btn-primary w-100" disabled={!totpCode || totpVerifyLoading}>
              {totpVerifyLoading ? 'Verifying...' : 'Verify'}
            </button>
          </form>
        )}
        {!supportHandoffLoading && step === 'force-password' && (
          <form onSubmit={handleForcePasswordSubmit}>
            <div className="mb-2 text-muted text-center">
              {forcePasswordReason === 'enroll'
                ? 'Set a password to finish signing in.'
                : 'Update your password to continue.'}
            </div>
            <div className="mb-3">
              <label className="form-label">New Password</label>
              <input
                type="password"
                className="form-control"
                value={forcePassword}
                onChange={(e) => setForcePassword(e.target.value)}
                autoFocus
              />
              <div className="form-text">Minimum 8 characters, 1 uppercase, 1 special character.</div>
            </div>
            <div className="mb-3">
              <label className="form-label">Confirm New Password</label>
              <input
                type="password"
                className="form-control"
                value={forcePasswordConfirm}
                onChange={(e) => setForcePasswordConfirm(e.target.value)}
              />
            </div>
            <button type="submit" className="btn btn-primary w-100" disabled={forcePasswordLoading}>
              {forcePasswordLoading ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        )}
        {!supportHandoffLoading && step === 'otp' && (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setError('');
              setOtpLoading(true);
              try {
                await api.post('/acct-mgmt/v1/login/otp/request', { email });
                setOtpSent(true);
                setOtpCode('');
                setOtpResendCooldown(30);
                setStep('otp-verify');
              } catch (err) {
                setError(getApiErrorMessage(err, 'Failed to send one-time code'));
              } finally {
                setOtpLoading(false);
              }
            }}
          >
            <div className="mb-2 text-muted text-center">We’ll send a one-time code to {email}.</div>
            <button type="submit" className="btn btn-primary w-100" disabled={otpLoading}>
              {otpLoading ? 'Sending...' : 'Send Code'}
            </button>
            <button
              type="button"
              className="btn btn-link mt-2 w-100"
              onClick={() => setStep('choose')}
            >
              Back to Sign-In Options
            </button>
          </form>
        )}
        {!supportHandoffLoading && step === 'otp-verify' && (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setError('');
              beginSupportHandoff();
              try {
                const response = await api.post('/acct-mgmt/v1/login/otp/verify', { email, code: otpCode, return_to: returnTo });
                if (response.data?.force_password_reset) {
                  endSupportHandoff();
                  setForcePasswordToken(response.data.reset_token || '');
                  setForcePasswordReason(response.data.reset_reason || 'reset');
                  setStep('force-password');
                  return;
                }
                if (response.data?.redirect_url || (response.data.access_token && response.data.refresh_token)) {
                  redirectAfterAuth(response.data.access_token, response.data.refresh_token, response.data);
                  return;
                }
                if (response.data.invite_code) {
                  endSupportHandoff();
                  history.push(`/signup?email=${encodeURIComponent(email)}&invite_code=${response.data.invite_code}&resume=1`);
                  return;
                }
                if (response.data.needs_org_setup) {
                  endSupportHandoff();
                  history.push(`/signup?email=${encodeURIComponent(email)}&resume=1`);
                  return;
                }
                endSupportHandoff();
                history.push('/account');
              } catch (err) {
                endSupportHandoff();
                if (err.response?.status === 403) {
                  setError(getPortalAccessErrorMessage(err));
                } else {
                  setError(getApiErrorMessage(err, 'Invalid or expired code'));
                }
                setOtpCode('');
                setOtpShake(true);
                setTimeout(() => setOtpShake(false), 500);
              }
            }}
          >
            <div className="mb-2 text-muted text-center">Enter the one-time code sent to {email}.</div>
            <div className="mb-3 text-center">
              <CodeInput value={otpCode} onChange={setOtpCode} disabled={otpLoading} shake={otpShake} autoFocus />
            </div>
            <button type="submit" className="btn btn-primary w-100" disabled={otpCode.length < 6}>Verify Code</button>
            <button
              type="button"
              className="btn btn-outline-secondary w-100 mt-2"
              disabled={otpResendCooldown > 0 || otpLoading}
              onClick={async () => {
                setError('');
                setOtpLoading(true);
                try {
                  await api.post('/acct-mgmt/v1/login/otp/request', { email });
                  setOtpCode('');
                  setOtpResendCooldown(30);
                } catch (err) {
                  setError(getApiErrorMessage(err, 'Failed to resend code'));
                } finally {
                  setOtpLoading(false);
                }
              }}
            >
              {otpResendCooldown > 0 ? `Resend Code (${otpResendCooldown}s)` : 'Resend Code'}
            </button>
          </form>
        )}
        {!supportHandoffLoading && step === 'email' && !isDemoReturn && (
          <p className="mt-4 text-center text-muted">
            Don’t have an account? <a href={`/signup?return_to=${encodeURIComponent(returnTo)}`} className="text-primary">Sign Up</a>
          </p>
        )}
        <div className="auth-footer">Finbase Single Sign-On: one account for all Finbase apps.</div>
      </div>
    </div>
  );
};

export default Login;
